/**
 * ProviderForm Component
 * 
 * Form for creating or editing a tenant-owned LLM provider.
 * API key is never shown after initial save.
 */

import { useState, useEffect } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { TenantProvider, TenantProviderCreate, TenantProviderUpdate, ProviderType } from '../types';
import { PROVIDER_METADATA } from '../types';
import { ProviderIcon } from './ProviderIcons';

interface ProviderFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TenantProviderCreate | TenantProviderUpdate) => Promise<void>;
  provider?: TenantProvider | null;
  isLoading?: boolean;
}

export function ProviderForm({
  open,
  onOpenChange,
  onSubmit,
  provider,
  isLoading = false,
}: ProviderFormProps) {
  const isEditing = !!provider;
  
  // Form state
  const [name, setName] = useState('');
  const [providerType, setProviderType] = useState<ProviderType>('azure_foundry');
  const [endpoint, setEndpoint] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [supportsStreaming, setSupportsStreaming] = useState(true);
  const [supportsTools, setSupportsTools] = useState(true);
  const [supportsVision, setSupportsVision] = useState(false);
  const [validateOnCreate, setValidateOnCreate] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when dialog opens/closes or provider changes
  useEffect(() => {
    if (open) {
      if (provider) {
        setName(provider.name);
        setProviderType(provider.provider_type);
        setEndpoint(provider.endpoint || '');
        setApiVersion(provider.api_version || '');
        setApiKey('');
        setSupportsStreaming(provider.supports_streaming);
        setSupportsTools(provider.supports_tools);
        setSupportsVision(provider.supports_vision);
      } else {
        setName('');
        setProviderType('azure_foundry');
        setEndpoint('');
        setApiVersion('');
        setApiKey('');
        setSupportsStreaming(true);
        setSupportsTools(true);
        setSupportsVision(false);
        setValidateOnCreate(true);
      }
      setShowApiKey(false);
      setErrors({});
    }
  }, [open, provider]);

  const metadata = PROVIDER_METADATA[providerType];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!/^[\w\s-]+$/.test(name)) {
      newErrors.name = 'Name can only contain letters, numbers, spaces, and hyphens';
    }

    if (!isEditing && !apiKey.trim()) {
      newErrors.apiKey = 'API key is required';
    }

    if (metadata.requiresEndpoint && !endpoint.trim()) {
      newErrors.endpoint = 'Endpoint is required for this provider';
    }

    if (metadata.requiresApiVersion && !apiVersion.trim()) {
      newErrors.apiVersion = 'API version is required for this provider';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    if (isEditing) {
      const updateData: TenantProviderUpdate = {
        name: name !== provider.name ? name : undefined,
        endpoint: endpoint !== (provider.endpoint || '') ? endpoint || undefined : undefined,
        api_version: apiVersion !== (provider.api_version || '') ? apiVersion || undefined : undefined,
        api_key: apiKey || undefined,
        supports_streaming: supportsStreaming !== provider.supports_streaming ? supportsStreaming : undefined,
        supports_tools: supportsTools !== provider.supports_tools ? supportsTools : undefined,
        supports_vision: supportsVision !== provider.supports_vision ? supportsVision : undefined,
      };
      
      // Remove undefined fields
      const cleanedData = Object.fromEntries(
        Object.entries(updateData).filter(([, v]) => v !== undefined)
      );
      
      await onSubmit(cleanedData);
    } else {
      const createData: TenantProviderCreate = {
        name,
        provider_type: providerType,
        endpoint: endpoint || undefined,
        api_version: apiVersion || undefined,
        api_key: apiKey,
        supports_streaming: supportsStreaming,
        supports_tools: supportsTools,
        supports_vision: supportsVision,
        validate_on_create: validateOnCreate,
      };
      
      await onSubmit(createData);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit Provider' : 'Add Your LLM Provider'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update your provider configuration. Leave API key empty to keep the current one.'
                : 'Configure your own LLM provider credentials. Your API key will be stored securely and never displayed again.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Provider Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">Provider Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My OpenAI Provider"
                className={cn(errors.name && 'border-red-500')}
                autoComplete="off"
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Provider Type (disabled when editing) */}
            <div className="grid gap-2">
              <Label htmlFor="provider_type">Provider Type</Label>
              <Select
                value={providerType}
                onValueChange={(value) => setProviderType(value as ProviderType)}
                disabled={isEditing}
              >
                <SelectTrigger id="provider_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(PROVIDER_METADATA).map((meta) => (
                    <SelectItem key={meta.type} value={meta.type}>
                      <span className="flex items-center gap-2">
                        <ProviderIcon
                          providerType={meta.type}
                          size={16}
                          className="w-4 h-4 object-contain"
                        />
                        <span>{meta.displayName}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {metadata.description}
              </p>
            </div>

            {/* Endpoint (if required) */}
            {metadata.requiresEndpoint && (
              <div className="grid gap-2">
                <Label htmlFor="endpoint">Endpoint URL</Label>
                <Input
                  id="endpoint"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  placeholder="https://your-resource.openai.azure.com/"
                  className={cn(errors.endpoint && 'border-red-500')}
                  autoComplete="off"
                />
                {errors.endpoint && (
                  <p className="text-sm text-red-500">{errors.endpoint}</p>
                )}
              </div>
            )}

            {/* API Version (if required) */}
            {metadata.requiresApiVersion && (
              <div className="grid gap-2">
                <Label htmlFor="api_version">API Version</Label>
                <Input
                  id="api_version"
                  value={apiVersion}
                  onChange={(e) => setApiVersion(e.target.value)}
                  placeholder="2024-06-01"
                  className={cn(errors.apiVersion && 'border-red-500')}
                  autoComplete="off"
                />
                {errors.apiVersion && (
                  <p className="text-sm text-red-500">{errors.apiVersion}</p>
                )}
              </div>
            )}

            {/* API Key (with toggle visibility) */}
            <div className="grid gap-2">
              <Label htmlFor="api_key">
                API Key
                {isEditing && (
                  <span className="text-muted-foreground font-normal ml-2">
                    (leave empty to keep current)
                  </span>
                )}
              </Label>
              <div className="relative">
                <Input
                  id="api_key"
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={isEditing ? '••••••••••••••••' : 'sk-...'}
                  className={cn('pr-10', errors.apiKey && 'border-red-500')}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                >
                  {showApiKey ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </div>
              {errors.apiKey && (
                <p className="text-sm text-red-500">{errors.apiKey}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Your API key will be stored securely and never displayed again.
              </p>
            </div>

            {/* Capabilities checkboxes */}
            <div className="grid gap-2">
              <Label>Capabilities</Label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={supportsStreaming}
                    onChange={(e) => setSupportsStreaming(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Streaming</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={supportsTools}
                    onChange={(e) => setSupportsTools(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Tool Calling</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={supportsVision}
                    onChange={(e) => setSupportsVision(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Vision</span>
                </label>
              </div>
            </div>

            {/* Validate on create (only for new providers) */}
            {!isEditing && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={validateOnCreate}
                  onChange={(e) => setValidateOnCreate(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm">Validate credentials on save</span>
              </label>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : isEditing ? (
                'Save Changes'
              ) : (
                'Add Provider'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
